import { all, call, put, takeLatest } from 'redux-saga/effects';

import {
  getInviteLandlord,
  getVP,
  getRM,
  getPM,
  getUserApp,
  getUserPolicy,
  getBuilding,
  getUserClaim,
  delUsers,
  getActiveLandlord,
  updateProperty,
  deleteLandlord,
  getLandlordTree,
  getVPTree,
  getRMTree,
  getUserBuildingSearch,
  setDelegationMoveOut,
  getUserList,
  getLandlordDetail,
  getDelegationDetail,
} from './APIs/user';
import types from '../constants/userConstant';
import {
  setActiveLandlordAction,
  setActiveLandlordErrorAction,
  getVPAction,
  getRMAction,
  getPMAction,
  setInviteLandlordAction,
  setVPAction,
  setVPListErrorAction,
  setPMAction,
  setRMAction,
  setRMListErrorAction,
  setPMListErrorAction,
  setLandlordTreeAction,
  setVPTreeAction,
  setUserBuildingSearchAction,
  getLandlordTreeAction,
  getVPTreeAction,
  setRMTreeAction,
  getRMTreeAction,
  setTreeLoadingAction,
  setTreeFetchErrorAction,
  getUserBuildingSearchAction,
  setUserListAction,
  setLandlordDetailAction,
  setLandlordDetailErrorAction,
  setDelegationDetailAction,
  setDelegationDetailErrorAction,
  getDelegationDetailAction,
  updateUserPropertySuccessAction,
  updateUserPropertyFailureAction,
} from '../actions/userActions';
import {
  setAppAction,
  setClaimAction,
  setBuildingAction,
  setPolicyAction,
} from '../actions/propertyActions';
import { successNotif, failedNotif } from '../actions/notifActions';
import { getApiErrorMessage } from 'utils/apiError';

function* getInviteLandlordSaga({ payload }) {
  const isRefresh = payload?.type === 'refresh';
  try {
    const data = yield getInviteLandlord(payload);
    yield put(setInviteLandlordAction(data));
    if (isRefresh) {
      yield put(successNotif('Operator list refreshed from Salesforce successfully.'));
    }
  } catch (error) {
    const message = getApiErrorMessage(
      error,
      isRefresh
        ? 'Failed to refresh landlord list from Salesforce.'
        : 'Failed to load landlord list.',
    );
    yield put(failedNotif(message));
  }
}

function* getVPSaga({ payload }) {
  try {
    yield put(setVPListErrorAction(false));
    const data = yield getVP(payload);
    yield put(setVPAction(data));
  } catch (error) {
    yield put(setVPListErrorAction(true));
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to load Full Portfolio users.')));
  }
}

function* getRMSaga({ payload }) {
  try {
    yield put(setRMListErrorAction(false));
    const data = yield getRM(payload);
    yield put(setRMAction(data));
  } catch (error) {
    yield put(setRMListErrorAction(true));
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to load Multi-Site users.')));
  }
}

function* getPMSaga({ payload }) {
  try {
    yield put(setPMListErrorAction(false));
    const data = yield getPM(payload);
    yield put(setPMAction(data));
  } catch (error) {
    yield put(setPMListErrorAction(true));
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to load Property Manager users.')));
  }
}

function* onGetInviteLandlord() {
  yield takeLatest(types.GET_INVITE_LL, getInviteLandlordSaga);
}

function* onGetVP() {
  yield takeLatest(types.GET_VP, getVPSaga);
}

function* onGetRM() {
  yield takeLatest(types.GET_RM, getRMSaga);
}

function* onGetPM() {
  yield takeLatest(types.GET_PM, getPMSaga);
}

function* getUserBuildingSaga({ payload }) {
  try {
    const data = yield getBuilding(payload);
    yield put(setBuildingAction(data));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to load user buildings.')));
  }
}

function* getUserAppSaga({ payload }) {
  try {
    const data = yield getUserApp(payload);
    yield put(setAppAction(data));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to load user applications.')));
  }
}

function* getUserPolicySaga({ payload }) {
  try {
    const data = yield getUserPolicy(payload);
    yield put(setPolicyAction(data));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to load user policies.')));
  }
}

function* getUserClaimSaga({ payload }) {
  try {
    const data = yield getUserClaim(payload);
    yield put(setClaimAction(data));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to load user claims.')));
  }
}

function* delUsersSaga({ payload: { data, role, search, listQuery } }) {
  try {
    const res = yield delUsers(data);
    yield put(successNotif(res.message));
    if (search) {
      yield put(getUserBuildingSearchAction(search));
    } else if (role === 'vp') {
      yield put(getVPAction(listQuery));
    } else if (role === 'rm') {
      yield put(getRMAction(listQuery));
    } else if (role === 'pm') {
      yield put(getPMAction(listQuery));
    }
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to delete user.')));
  }
}

function* updateUserPropertySaga({ payload }) {
  try {
    const res = yield updateProperty(payload);
    yield put(successNotif(res));
    yield put(updateUserPropertySuccessAction());
    if (payload.delegation?.id) {
      yield put(getDelegationDetailAction(payload.delegation.id));
    }
    if (payload.delegation.accepter_role === 'vp') {
      yield put(getVPAction());
    } else if (payload.delegation.accepter_role === 'rm') {
      yield put(getRMAction());
    } else if (payload.delegation.accepter_role === 'pm') {
      yield put(getPMAction());
    }
  } catch (error) {
    yield put(updateUserPropertyFailureAction());
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to save property changes.')));
  }
}

function* getActiveLandlordSaga({ payload }) {
  try {
    yield put(setActiveLandlordErrorAction(false));
    const data = yield getActiveLandlord(payload);
    yield put(setActiveLandlordAction(data));
  } catch (error) {
    yield put(setActiveLandlordErrorAction(true));
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to load companies.')));
  }
}

function* getLandlordDetailSaga({ payload }) {
  try {
    const data = yield getLandlordDetail(payload);
    yield put(setLandlordDetailAction(data));
  } catch (error) {
    yield put(setLandlordDetailErrorAction());
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to fetch landlord detail.')));
  }
}

function* getDelegationDetailSaga({ payload }) {
  try {
    const data = yield getDelegationDetail(payload);
    yield put(setDelegationDetailAction(data));
  } catch (error) {
    yield put(setDelegationDetailErrorAction());
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to fetch user detail.')));
  }
}

function* onGetUserBuilding() {
  yield takeLatest(types.GET_USER_BUILDING, getUserBuildingSaga);
}

function* onGetUserApp() {
  yield takeLatest(types.GET_USER_APP, getUserAppSaga);
}

function* onGetUserPolicy() {
  yield takeLatest(types.GET_USER_POLICY, getUserPolicySaga);
}

function* onGetUserClaim() {
  yield takeLatest(types.GET_USER_CLAIM, getUserClaimSaga);
}

function* onDelUsers() {
  yield takeLatest(types.DEL_USERS, delUsersSaga);
}

function* onUpdateUserProperty() {
  yield takeLatest(types.UPDATE_USER_PROPERTY, updateUserPropertySaga);
}

function* onGetActiveLandlord() {
  yield takeLatest(types.GET_ACTIVE_LL, getActiveLandlordSaga);
}

function* onGetLandlordDetail() {
  yield takeLatest(types.GET_LANDLORD_DETAIL, getLandlordDetailSaga);
}

function* onGetDelegationDetail() {
  yield takeLatest(types.GET_DELEGATION_DETAIL, getDelegationDetailSaga);
}

function* deleteLandlordSaga({ payload }) {
  try {
    const data = yield deleteLandlord(payload);
    yield put(setActiveLandlordAction(data));
    yield put(successNotif(data?.message || 'Operator deleted successfully.'));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to delete operator.')));
  }
}

function* onDeleteLandlord() {
  yield takeLatest(types.DEL_LANDLORD, deleteLandlordSaga);
}

function* getLandlordTreeSaga() {
  try {
    yield put(setTreeFetchErrorAction(false));
    const data = yield getLandlordTree();
    yield put(setLandlordTreeAction(data));
  } catch (error) {
    yield put(setTreeFetchErrorAction(true));
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to load organization tree.')));
  }
}

function* onGetLandlordTree() {
  yield takeLatest(types.GET_LANDLORDTREE, getLandlordTreeSaga);
}

function* getVPTreeSaga() {
  try {
    yield put(setTreeFetchErrorAction(false));
    const data = yield getVPTree();
    yield put(setVPTreeAction(data));
  } catch (error) {
    yield put(setTreeFetchErrorAction(true));
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to load organization tree.')));
  }
}

function* onGetVPTree() {
  yield takeLatest(types.GET_VPTREE, getVPTreeSaga);
}

function* getRMTreeSaga() {
  try {
    yield put(setTreeFetchErrorAction(false));
    const data = yield getRMTree();
    yield put(setRMTreeAction(data));
  } catch (error) {
    yield put(setTreeFetchErrorAction(true));
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to load organization tree.')));
  }
}

function* onGetRMTree() {
  yield takeLatest(types.GET_RMTREE, getRMTreeSaga);
}

function* getUserBuildingSearchSaga({ payload }) {
  try {
    const data = yield getUserBuildingSearch(payload);
    yield put(setUserBuildingSearchAction(data));
  } catch (error) {
    yield put(setUserBuildingSearchAction([]));
    yield put(failedNotif(getApiErrorMessage(error, 'User building search failed.')));
  }
}

function* onGetUserBuildingSearch() {
  yield takeLatest(types.GET_USER_BUILDING_SEARCH, getUserBuildingSearchSaga);
}

function* setDelegationMoveOutSaga({ payload }) {
  try {
    yield put(setTreeLoadingAction(true));
    const data = yield setDelegationMoveOut(payload);
    yield put(successNotif(data));
    if (payload.editType === 'landlord') {
      yield put(getLandlordTreeAction());
    } else if (payload.editType === 'vp') {
      yield put(getVPTreeAction());
    } else if (payload.editType === 'rm') {
      yield put(getRMTreeAction());
    }
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to save tree changes.')));
  } finally {
    yield put(setTreeLoadingAction(false));
  }
}

function* onSetDelegationMoveOut() {
  yield takeLatest(types.DELEGATION_MOVEOUT, setDelegationMoveOutSaga);
}

function* getUserListSaga({ payload }) {
  try {
    const data = yield getUserList(payload);
    yield put(setUserListAction(data));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to load user list.')));
  }
}

function* onGetUserList() {
  yield takeLatest(types.GET_USER_LIST, getUserListSaga);
}

export default function* userSagas() {
  yield all([
    call(onGetInviteLandlord),
    call(onGetVP),
    call(onGetRM),
    call(onGetPM),
    call(onGetUserBuilding),
    call(onGetUserApp),
    call(onGetUserPolicy),
    call(onGetUserClaim),
    call(onDelUsers),
    call(onUpdateUserProperty),
    call(onGetActiveLandlord),
    call(onGetLandlordDetail),
    call(onGetDelegationDetail),
    call(onDeleteLandlord),
    call(onGetLandlordTree),
    call(onGetVPTree),
    call(onGetRMTree),
    call(onGetUserBuildingSearch),
    call(onSetDelegationMoveOut),
    call(onGetUserList),
  ]);
}
